/* Comparison Studio progressive enhancement.
   Storage stays on this device; all durable writes require an explicit button. */
(() => {
  'use strict';
  const READING_KEY = 'bk-comparison-native-reading-v1:' + (window.BKComparisonNative?.locale || 'en');
  const NOTES_KEY = 'bk-comparison-notes-v1';
  const NOTE_FIELDS = ['left', 'right', 'question', 'observations', 'next', 'date'];
  const NOTE_LABELS = {
    left: 'First option', right: 'Second option', question: 'My decision question',
    observations: 'What I noticed', next: 'My next step', date: 'Date'
  };
  const queryAll = (selector, root = document.querySelector('[data-bk-comparison-guide]') || document) => Array.from(root.querySelectorAll(selector));
  const announce = (node, message, state = 'ready') => {
    if (!node) return;
    node.textContent = message;
    node.dataset.state = state;
    if (!node.hasAttribute('role')) node.setAttribute('role', 'status');
    if (!node.hasAttribute('aria-live')) node.setAttribute('aria-live', 'polite');
  };
  const announceSaved = (message, state) => queryAll('[data-save-status]').forEach(node => announce(node, message, state));
  const fold = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase();
  const safeReadingHref = value => window.BKComparisonNative?.safeReadingHref(value) || null;
  const readJSON = (key, fallback, valid) => {
    let raw = null;
    try {
      raw = window.localStorage.getItem(key);
      if (raw === null) return { value: fallback, raw, error: null };
      const parsed = JSON.parse(raw);
      if (!valid(parsed)) throw new Error('Unrecognised saved data');
      return { value: parsed, raw, error: null };
    } catch (error) { return { value: fallback, raw, error }; }
  };
  const persist = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch (_) { return false; }
  };
  const restoreRaw = (key, raw) => {
    try {
      if (raw === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, raw);
      return true;
    } catch (_) { return false; }
  };
  const validReading = value => Array.isArray(value) && value.length <= 100 && value.every(item =>
    item && typeof item === 'object' && /^[A-Z]\d{2}$/.test(item.id) &&
    typeof item.title === 'string' && item.title.length <= 500 &&
    typeof item.href === 'string' && Boolean(safeReadingHref(item.href))
  );
  const validNotes = value => value && typeof value === 'object' && !Array.isArray(value) &&
    Object.entries(value).every(([key, note]) => key.length <= 500 && note && typeof note === 'object' && !Array.isArray(note) &&
      NOTE_FIELDS.every(name => typeof note[name] === 'string' && note[name].length <= 50000)
    );

  const initLibrary = () => {
    queryAll('[data-library-filter]').forEach(form => {
      const input = form.querySelector('[data-search]');
      const buttons = queryAll('[data-category-choice]', form);
      if (!input || !buttons.length) return;
      const section = form.closest('section');
      const entries = queryAll('[data-entry]', section);
      const status = form.querySelector('[data-results]');
      const empty = section.querySelector('[data-empty]');
      const more = section.querySelector('[data-show-more]');
      let chosen = '', limit = 12, matches = [];
      const update = () => {
        const words = fold(input.value).trim().split(/\s+/).filter(Boolean);
        matches = entries.filter(entry => (!chosen || entry.dataset.category === chosen) && words.every(word => fold(entry.dataset.search || entry.textContent).includes(word)));
        const visible = new Set(matches.slice(0, limit));
        entries.forEach(entry => { entry.hidden = !visible.has(entry); });
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.categoryChoice === chosen)));
        const count = Math.min(limit, matches.length);
        announce(status, matches.length ? `Showing ${count} of ${matches.length} comparisons${chosen || words.length ? ' matching your choices' : ''}.` : 'No comparisons match your choices.');
        if (empty) empty.hidden = matches.length !== 0;
        if (more) { more.hidden = count >= matches.length; more.textContent = `Show ${Math.min(12, matches.length - count)} more comparisons`; }
      };
      const reset = () => { input.value = ''; chosen = ''; limit = 12; update(); input.focus({preventScroll: true}); };
      input.addEventListener('input', () => { limit = 12; update(); });
      buttons.forEach(button => button.addEventListener('click', () => { chosen = button.dataset.categoryChoice; limit = 12; update(); }));
      form.addEventListener('submit', event => { event.preventDefault(); limit = 12; update(); });
      form.querySelector('[data-reset]')?.addEventListener('click', reset);
      section.querySelector('[data-clear-empty]')?.addEventListener('click', reset);
      if (more) more.addEventListener('click', () => {
        const firstNew = matches[limit]; limit += 12; update();
        firstNew?.querySelector('a')?.focus({preventScroll: true});
        firstNew?.scrollIntoView({block: 'start', behavior: 'auto'});
      });
      form.hidden = false;
      update();
    });
  };

  const initReadingList = () => {
    const buttons = queryAll('[data-save-reading]');
    const containers = queryAll('[data-saved-reading]');
    if (!buttons.length && !containers.length) return;
    let state = readJSON(READING_KEY, [], validReading);
    let undo = null;
    const updateButtons = () => {
      buttons.forEach(button => {
        const selected = state.value.some(item => item.id === button.dataset.id);
        button.setAttribute('aria-pressed', String(selected));
        button.textContent = button.hasAttribute('data-save-compact') ? (selected ? 'Saved' : 'Save') : (selected ? 'Saved · remove' : 'Save guide');
        button.setAttribute('aria-label', selected ? 'Remove this guide from saved reading' : 'Save this guide');
      });
    };
    const render = () => {
      updateButtons();
      containers.forEach(container => {
        const fragment = document.createDocumentFragment();
        const heading = document.createElement('h2');
        heading.textContent = 'Saved for later';
        fragment.append(heading);
        const explanation = document.createElement('p');
        explanation.textContent = state.error
          ? 'Your saved guides could not be read. You can clear this list and undo the clear.'
          : state.value.length ? 'Saved on this device for another reading moment.' : 'No saved guides yet. Open a comparison and choose Save guide.';
        fragment.append(explanation);
        if (state.value.length) {
          const list = document.createElement('ul');
          state.value.forEach(item => {
            const href = safeReadingHref(item.href);
            if (!href) return;
            const li = document.createElement('li');
            const link = document.createElement('a');
            link.href = href;
            link.textContent = item.title;
            li.append(link);
            list.append(li);
          });
          fragment.append(list);
        }
        if (state.value.length || state.error || undo) {
          const clear = document.createElement('button');
          clear.type = 'button';
          clear.dataset.clearSaved = '';
          clear.textContent = undo ? 'Undo reading-list clear' : 'Clear saved reading list';
          clear.addEventListener('click', () => {
            if (undo) {
              if (!restoreRaw(READING_KEY, undo.raw)) {
                announceSaved('The list could not be restored in device storage. The undo remains available.', 'error');
                return;
              }
              state = readJSON(READING_KEY, [], validReading);
              undo = null;
              render();
              announceSaved('Reading-list clear undone.');
              const restoredControl = container.querySelector('[data-clear-saved]');
              if (restoredControl) restoredControl.focus();
              else { container.setAttribute('tabindex', '-1'); container.focus(); }
              return;
            }
            const previous = readJSON(READING_KEY, [], validReading);
            if (!persist(READING_KEY, [])) {
              announceSaved('Device storage is unavailable. Your saved list has not been cleared.', 'error');
              return;
            }
            undo = previous;
            state = { value: [], raw: '[]', error: null };
            render();
            announceSaved('Saved reading list cleared on this device. You can undo this clear.');
            const undoButton = container.querySelector('[data-clear-saved]');
            if (undoButton) undoButton.focus();
          });
          fragment.append(clear);
        }
        container.replaceChildren(fragment);
        container.hidden = false;
      });
    };
    buttons.forEach(button => {
      button.type = 'button';
      button.addEventListener('click', () => {
        const latest = readJSON(READING_KEY, [], validReading);
        if (latest.error) {
          state = latest;
          render();
          announceSaved('Saved reading could not be read. Use Clear saved reading list to start again; the clear can be undone.', 'error');
          return;
        }
        const id = button.dataset.id;
        const href = safeReadingHref(button.dataset.href || window.location.href);
        const title = (button.dataset.title || document.querySelector('.cs-app h1')?.textContent || '').trim();
        if (!/^[A-Z]\d{2}$/.test(id || '') || !href || !title) {
          announceSaved('This guide could not be saved. You can use your browser bookmark instead.', 'error');
          return;
        }
        const existing = latest.value.some(item => item.id === id);
        const next = existing ? latest.value.filter(item => item.id !== id) :
          [...latest.value, { id, title: title.slice(0, 500), href, savedAt: new Date().toISOString() }];
        if (!persist(READING_KEY, next)) {
          announceSaved('This device could not save the reading list. Your previous saved list is unchanged; you can use a browser bookmark.', 'error');
          return;
        }
        state = { value: next, raw: JSON.stringify(next), error: null };
        undo = null;
        render();
        announceSaved(existing ? 'Guide removed from this device’s reading list.' : 'Guide saved in this device’s reading list.');
      });
      button.hidden = false;
    });
    window.addEventListener('storage', event => {
      if (event.key !== READING_KEY && event.key !== null) return;
      state = readJSON(READING_KEY, [], validReading);
      undo = null;
      render();
      announceSaved(state.error ? 'The reading list could not be read after a change in another tab.' : 'Reading list updated from another tab.', state.error ? 'error' : 'ready');
    });
    render();
    if (state.error) announceSaved('Saved reading is unavailable. All guides remain readable.', 'error');
  };

  const preparePrint = (section, fields) => {
    let copy = section.querySelector('.cs-notebook-print');
    if (!copy) {
      copy = document.createElement('div');
      copy.className = 'cs-notebook-print';
      section.append(copy);
    }
    const list = document.createElement('dl');
    NOTE_FIELDS.forEach(name => {
      const term = document.createElement('dt');
      const value = document.createElement('dd');
      term.textContent = NOTE_LABELS[name];
      value.textContent = fields[name] || 'Not recorded';
      list.append(term, value);
    });
    copy.replaceChildren(list);
  };
  const clearPrintMode = () => {
    document.documentElement.classList.remove('cs-print-notebook');
    queryAll('.cs-print-parent, .cs-print-target').forEach(node => node.classList.remove('cs-print-parent', 'cs-print-target'));
  };
  const initNotebooks = () => {
    queryAll('[data-notebook]').forEach(section => {
      const form = section.querySelector('form');
      if (!form || NOTE_FIELDS.some(name => !form.elements.namedItem(name))) return;
      const status = section.querySelector('[data-notebook-status]');
      const clear = section.querySelector('[data-clear-notes]');
      const print = section.querySelector('[data-print-notes]');
      const pageKey = section.dataset.id || document.querySelector('[data-save-reading]')?.dataset.id || window.location.pathname;
      const fields = () => Object.fromEntries(NOTE_FIELDS.map(name => [name, String(form.elements.namedItem(name).value || '')]));
      const apply = value => NOTE_FIELDS.forEach(name => { form.elements.namedItem(name).value = value[name] || ''; });
      let undo = null;
      const clearLabel = clear?.textContent || 'Clear my saved note';
      const initial = readJSON(NOTES_KEY, {}, validNotes);
      const hasDraft = NOTE_FIELDS.filter(name => name !== 'date').some(name => fields()[name].trim());
      if (!initial.error && initial.value[pageKey] && !hasDraft) {
        apply(initial.value[pageKey]);
        announce(status, 'Your explicitly saved note has been restored from this device.');
      } else if (initial.error) {
        announce(status, 'Saved notes could not be read. You can still write and print; your text will not be saved automatically.', 'error');
      }
      section.dataset.enhanced = 'true';
      preparePrint(section, fields());
      form.addEventListener('input', () => {
        announce(status, 'Your changes are not saved yet. Choose Save note to keep them on this device.');
      });
      form.addEventListener('submit', event => {
        event.preventDefault();
        const current = readJSON(NOTES_KEY, {}, validNotes);
        if (current.error) {
          announce(status, 'Existing notes could not be read, so they were not overwritten. Your text remains here and can be printed.', 'error');
          return;
        }
        const next = Object.assign(Object.create(null), current.value);
        next[pageKey] = { ...fields(), savedAt: new Date().toISOString() };
        if (!persist(NOTES_KEY, next)) {
          announce(status, 'This device could not save your note. Your text remains in the form; print or copy it before leaving.', 'error');
          return;
        }
        undo = null;
        if (clear) clear.textContent = clearLabel;
        preparePrint(section, fields());
        announce(status, 'Note saved on this device. Later edits need another explicit save.');
      });
      if (clear) {
        clear.type = 'button';
        clear.addEventListener('click', () => {
          const current = readJSON(NOTES_KEY, {}, validNotes);
          if (current.error) {
            announce(status, 'Saved notes could not be read, so no notes were cleared. Your text remains available to print.', 'error');
            return;
          }
          const next = Object.assign(Object.create(null), current.value);
          if (undo) {
            if (undo.saved) next[pageKey] = undo.saved;
            else delete next[pageKey];
            if (!persist(NOTES_KEY, next)) {
              announce(status, 'The clear could not be undone in device storage. The undo remains available.', 'error');
              return;
            }
            apply(undo.draft);
            undo = null;
            clear.textContent = clearLabel;
            preparePrint(section, fields());
            announce(status, 'Clear undone. Your previous text and saved note have been restored.');
            return;
          }
          const previous = { draft: fields(), saved: current.value[pageKey] || null };
          delete next[pageKey];
          if (!persist(NOTES_KEY, next)) {
            announce(status, 'This device could not clear the saved note. Your text and previous saved note are unchanged.', 'error');
            return;
          }
          undo = previous;
          apply(Object.fromEntries(NOTE_FIELDS.map(name => [name, ''])));
          clear.textContent = 'Undo note clear';
          preparePrint(section, fields());
          announce(status, 'This comparison note cleared. You can undo the clear; notes for other comparisons remain saved.');
        });
        clear.hidden = false;
      }
      if (print) {
        print.type = 'button';
        print.addEventListener('click', () => {
          clearPrintMode();
          preparePrint(section, fields());
          section.classList.add('cs-print-target');
          let parent = section.parentElement;
          while (parent && parent !== document.documentElement) {
            parent.classList.add('cs-print-parent');
            parent = parent.parentElement;
          }
          document.documentElement.classList.add('cs-print-notebook');
          try { window.print(); }
          catch (_) {
            clearPrintMode();
            announce(status, 'Printing is unavailable here. You can select and copy your note text.', 'error');
          }
        });
        print.hidden = false;
      }
      queryAll('button[type="submit"], input[type="submit"]', form).forEach(button => { button.hidden = false; });
      window.addEventListener('beforeprint', () => preparePrint(section, fields()));
    });
    window.addEventListener('afterprint', clearPrintMode);
  };

  const initRowFilters = () => {
    queryAll('[data-row-filter]').forEach(wrapper => {
      const buttons = queryAll('[data-dimension-choice]', wrapper);
      const section = wrapper.closest('section');
      const rows = queryAll('tr[data-dimension]', section);
      const status = section.querySelector('[data-row-status]');
      if (!buttons.length || !rows.length) return;
      const update = selected => {
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button === selected)));
        rows.forEach(row => { row.hidden = selected.dataset.dimensionChoice !== '' && row.dataset.dimension !== selected.dataset.dimensionChoice; });
        announce(status, selected.dataset.dimensionChoice === '' ? `All ${rows.length} comparison questions shown.` : `Showing ${selected.textContent.trim().toLowerCase()}. Choose All differences to see every question.`);
      };
      buttons.forEach(button => button.addEventListener('click', () => update(button)));
      wrapper.hidden = false;
      update(buttons[0]);
    });
  };

  const initCopy = () => {
    queryAll('[data-copy]').forEach(button => {
      const local = button.closest('.cs-reading-tools, .cs-hero-actions') || button.parentElement;
      const status = local?.querySelector('[data-copy-status]') || document.querySelector('[data-copy-status]');
      button.type = 'button';
      button.addEventListener('click', async () => {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(window.location.origin + window.location.pathname);
          announce(status, 'Link copied.');
        } catch (_) {
          announce(status, 'Copying is unavailable here. Select and copy the address in your browser’s address bar.', 'error');
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      });
      button.hidden = false;
    });
  };

  const initNavigation = () => {
    const compact = window.matchMedia('(max-width: 991.84px)');
    let anchorEpoch = 0;
    const cancelAnchorCorrection = () => { anchorEpoch++; };
    window.addEventListener('wheel', cancelAnchorCorrection, {passive:true});
    window.addEventListener('touchstart', cancelAnchorCorrection, {passive:true});
    window.addEventListener('keydown', event => { if (['PageUp','PageDown','Home','End','ArrowUp','ArrowDown',' '].includes(event.key)) cancelAnchorCorrection(); });
    const contents = document.querySelector('.cs-contents');
    const readingMap = document.querySelector('.cs-reading-map');
    if (readingMap) readingMap.dataset.enhanced = 'true';
    const readingLinks = queryAll('[data-reading-link]').map(link => ({
      link,
      target: document.getElementById(link.getAttribute('href').slice(1)),
    })).filter(entry => entry.target);
    const outlineRail = document.querySelector('.cs-reading-rail');
    const syncOutlineOffset = () => {
      const nativeOffset = window.BKComparisonNative?.syncHeaderOffset() || 0;
      const offset = nativeOffset + (compact.matches && outlineRail ? Math.ceil(outlineRail.getBoundingClientRect().height + 16) : 24);
      document.querySelector('.cs-app')?.style.setProperty('--cs-outline-offset', `${offset}px`);
    };
    const trigger = contents?.querySelector('summary');
    const syncTriggerHeight = () => {
      if (compact.matches && trigger) document.querySelector('.cs-app')?.style.setProperty('--cs-outline-trigger-height', `${Math.ceil(trigger.getBoundingClientRect().height)}px`);
    };
    const setContentsMode = () => {
      if (contents) contents.open = !compact.matches;
      syncOutlineOffset();
      syncTriggerHeight();
    };
    document.querySelector('.cs-app')?.addEventListener('bk:header-offset', () => { syncOutlineOffset(); });
    setContentsMode();
    compact.addEventListener('change', setContentsMode);
    document.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || link.target === '_blank') return;
      let url;
      try { url = new URL(link.href, window.location.href); } catch (_) { return; }
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search || !url.hash) return;
      let target;
      try { target = document.getElementById(decodeURIComponent(url.hash.slice(1))); } catch (_) { return; }
      if (!target) return;
      const secondary = link.closest('.cs-nav-more');
      if (secondary) secondary.open = false;
      if (compact.matches || link.closest('.cs-guide-map')) {
        const contents = link.closest('.cs-contents');
        const details = contents?.matches('details') ? contents : contents?.querySelector('details');
        if (details) details.open = false;
        syncOutlineOffset();
      }
      event.preventDefault();
      const anchorTicket = ++anchorEpoch;
      syncOutlineOffset();
      const jump = () => {
        syncOutlineOffset();
        const offset = parseFloat(getComputedStyle(document.querySelector('.cs-app')).getPropertyValue('--cs-outline-offset')) || 24;
        window.scrollTo({top: Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset), left: 0, behavior: 'instant'});
      };
      jump();
      history.pushState(null, '', url.hash);
      window.requestAnimationFrame(() => {
        // Native header switches to its sticky state on the first scroll frame.
        jump();
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        window.BKComparisonNative?.afterHeaderMotion(() => {
          if (anchorTicket === anchorEpoch && document.activeElement === target) jump();
        });
      });
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const secondary = event.target.closest('.cs-nav-more[open]');
      if (secondary) {
        secondary.open = false;
        secondary.querySelector('summary').focus();
      } else if (compact.matches && contents?.open && contents.contains(event.target)) {
        contents.open = false;
        contents.querySelector('summary').focus();
        syncOutlineOffset();
      }
    });
    document.addEventListener('pointerdown', event => {
      if (compact.matches && contents?.open && !readingMap.contains(event.target)) {
        contents.open = false;
        syncOutlineOffset();
      }
    });
    const article = document.querySelector('article.cs-prose, #reading');
    const progress = queryAll('[data-progress]');
    if (!article) return;
    const phases = queryAll('[data-reading-phase]');
    const percentLabels = queryAll('[data-reading-percent]');
    const tracks = queryAll('.cs-progress-track');
    let lastPercent = null;
    let scheduled = false;
    const update = () => {
      scheduled = false;
      syncOutlineOffset();
      const nativeOffset = window.BKComparisonNative?.headerOffset || 0;
      const activeThreshold = nativeOffset + (compact.matches && outlineRail ? outlineRail.getBoundingClientRect().height + 32 : 130);
      const bounds = article.getBoundingClientRect();
      const top = bounds.top + window.scrollY;
      const available = Math.max(1, bounds.height - window.innerHeight);
      const percent = Math.round(Math.min(100, Math.max(0, ((window.scrollY - top) / available) * 100)));
      if (percent !== lastPercent) {
        lastPercent = percent;
        percentLabels.forEach(label => { label.textContent = String(percent); });
        progress.forEach(button => {
          const span = button.querySelector('[data-percent]');
          if (span) span.textContent = String(percent);
          button.setAttribute('aria-label', `${percent}% through this article. Back to top instantly.`);
          button.title = `Back to top · ${percent}%`;
          button.style.setProperty('--cs-read-ring-offset', String(100 - percent));
        });
        tracks.forEach(track => {
          track.setAttribute('aria-valuenow', String(percent));
          track.setAttribute('aria-valuetext', `${percent}% through this article`);
          track.firstElementChild.style.width = `${percent}%`;
        });
      }
      let phase = phases[0];
      for (const link of phases) {
        const target = document.getElementById(link.hash.slice(1));
        if (target && target.getBoundingClientRect().top <= activeThreshold) phase = link;
      }
      phases.forEach(link => {
        if (link === phase) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      let current = readingLinks[0];
      for (const entry of readingLinks) {
        if (entry.target.getBoundingClientRect().top <= activeThreshold) current = entry;
      }
      readingLinks.forEach(entry => {
        if (entry === current) entry.link.setAttribute('aria-current', 'location');
        else entry.link.removeAttribute('aria-current');
      });
      const nav = readingMap?.querySelector('nav');
      const scrollFrame = compact.matches ? nav : outlineRail;
      if (contents?.open && scrollFrame && current && scrollFrame.scrollHeight > scrollFrame.clientHeight) {
        const linkBox = current.link.getBoundingClientRect(), frameBox = scrollFrame.getBoundingClientRect();
        if (linkBox.top < frameBox.top || linkBox.bottom > frameBox.bottom) scrollFrame.scrollTop += linkBox.top - frameBox.top - scrollFrame.clientHeight / 2 + linkBox.height / 2;
      }
      const currentLabel = document.querySelector('[data-current-section]');
      if (currentLabel && current) currentLabel.textContent = current.link.lastElementChild.textContent;
      if (readingMap) readingMap.dataset.docked = String(!compact.matches && readingMap.offsetHeight + 48 < window.innerHeight);
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(update);
    };
    progress.forEach(button => {
      button.type = 'button';
      button.hidden = false;
      button.addEventListener('click', () => {
        const heading = document.querySelector('.cs-app h1');
        if (!heading) return;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        update();
      });
    });
    queryAll('[data-reading-progress]').forEach(element => { element.hidden = false; });
    if (readingMap) {
      readingMap.addEventListener('toggle', () => {
        if (!compact.matches && contents && !contents.open) contents.open = true;
        syncOutlineOffset();
        syncTriggerHeight();
        schedule();
      }, true);
      if ('ResizeObserver' in window) new ResizeObserver(() => { syncOutlineOffset(); syncTriggerHeight(); schedule(); }).observe(readingMap);
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule, { once: true });
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(article);
    update();
  };

  const initialise = () => {
    initLibrary();
    initReadingList();
    // Note-taking is hidden by owner request; existing stored notes are left untouched.
    initRowFilters();
    initCopy();
    initNavigation();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
})();
